/** Read the JWT* AUTH issues without verifying it (the server verifies; the client only reads claims). */

export const READ_SCOPE = 'auth:tenants:read';
export const WRITE_SCOPE = 'auth:tenants:write';

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const bin = atob(padded);
  try {
    return decodeURIComponent(
      Array.from(bin, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    );
  } catch {
    return bin;
  }
}

export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    return JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** `exp` claim in milliseconds since the epoch, or 0 when the token cannot be read. */
export function jwtExpiryMs(token: string): number {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : 0;
}

const asList = (raw: unknown): string[] =>
  raw == null
    ? []
    : typeof raw === 'string'
      ? [raw]
      : Array.isArray(raw)
        ? raw.filter((r): r is string => typeof r === 'string')
        : [];

/** The space-separated `scope` claim (an array is accepted too). */
export function decodeTokenScopes(token: string): string[] {
  const raw = decodeJwt(token)?.['scope'];
  if (typeof raw === 'string') return raw.split(/\s+/).filter(Boolean);
  return asList(raw);
}

/**
 * Same rule as `ScopeAuthorizationExtensions.MatchesScope` in AUTH: exact match, or a granted `prefix:*`
 * satisfies every required scope under that prefix (`auth:*` and `auth:tenants:*` grant read and write).
 */
export function matchesScope(granted: string[], required: string): boolean {
  for (const scope of granted) {
    if (scope === required) return true;
    if (scope.endsWith(':*') && required.startsWith(scope.slice(0, -1))) return true;
  }
  return false;
}

/** What the current token lets the console do. */
export interface Access {
  /** `sub` — the user id (Database tenants) or code (Workserver). */
  subject: string;
  /** `name` identity claim forwarded from the identity provider. */
  name?: string;
  /** `tid` — the tenant the token was issued for. */
  tenantId?: string;
  /** `orgid` — the tenant's customer name. */
  orgId?: string;
  scopes: string[];
  /** The plural `roles` authority claim (from tenant_claims). */
  roles: string[];
  /** The singular `role` identity claim (from the identity provider). */
  identityRoles: string[];
  canRead: boolean;
  canWrite: boolean;
  expiresAt: number;
}

export function accessFromToken(token: string | null | undefined): Access {
  const claims = token ? (decodeJwt(token) ?? {}) : {};
  const scopes = token ? decodeTokenScopes(token) : [];
  const str = (k: string) => (typeof claims[k] === 'string' ? (claims[k] as string) : undefined);
  return {
    subject: str('sub') ?? '',
    name: str('name'),
    tenantId: str('tid'),
    orgId: str('orgid'),
    scopes,
    roles: asList(claims['roles']),
    identityRoles: asList(claims['role']),
    canRead: matchesScope(scopes, READ_SCOPE),
    canWrite: matchesScope(scopes, WRITE_SCOPE),
    expiresAt: token ? jwtExpiryMs(token) : 0,
  };
}
